import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./TradeEntryConfluenceStrategyTable.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

const BASE_SERVICE_URL = "/api/bot/ui/get";

function display(value) {
  return value === undefined || value === null || value === "" ? "-" : value;
}

function TradeEntryConfluenceStrategyTable({ onClose }) {
  const { workspace } = useWorkspace();
  const [strategies, setStrategies] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStrategies = useCallback(() => {
    setLoading(true);
    setError("");

    fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/confluenceStrategies`, workspace), {
      headers: getApiHeaders(),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        const normalized = ensureArray(data).map((item) => ({
          ...(item.strategy || item),
          rules: ensureArray(item.rules || item.strategyRules),
        }));
        setStrategies(normalized);
      })
      .catch((err) => {
        console.error("Confluence strategy fetch failed:", err);
        setError("Unable to load confluence strategies right now.");
        setStrategies([]);
      })
      .finally(() => setLoading(false));
  }, [workspace]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  const filteredStrategies = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text) {
      return strategies;
    }

    return strategies.filter((strategy) => {
      const ruleText = ensureArray(strategy.rules)
        .map((rule) => Object.values(rule).join(" "))
        .join(" ");
      return `${Object.values(strategy).join(" ")} ${ruleText}`.toLowerCase().includes(text);
    });
  }, [strategies, search]);

  return (
    <div className="confluence-strategy-container">
      <div className="confluence-strategy-header">
        <div>
          <h2>Trade Entry Confluence Strategies</h2>
          <p>Configured multi-timeframe rule sets used by Trade Entry Tracker.</p>
        </div>
        <div className="confluence-strategy-actions">
        <button className="btn-add" onClick={fetchStrategies} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        {onClose && <button type="button" className="panel-close-btn" onClick={onClose} aria-label="Close Confluence Strategies" title="Close Confluence Strategies" />}
        </div>
      </div>

      <div className="toolbar">
        <input
          className="search-box confluence-search"
          placeholder="Search strategy, signal, timeframe..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="confluence-summary">
        Loaded {filteredStrategies.length} confluence strateg{filteredStrategies.length === 1 ? "y" : "ies"}
      </div>

      <div className="confluence-strategy-list">
        {filteredStrategies.length === 0 && (
          <div className="empty-state">{loading ? "Loading confluence strategies..." : "No confluence strategies found."}</div>
        )}

        {filteredStrategies.map((strategy) => (
          <section className="confluence-strategy-card" key={strategy.strategyCode || strategy.id}>
            <div className="confluence-strategy-title-row">
              <div>
                <h3>{display(strategy.strategyName)}</h3>
                <span className="strategy-code">{display(strategy.strategyCode)}</span>
              </div>
              <div className="strategy-meta">
                <span>Final TF: {display(strategy.finalTriggerTimeFrame)}</span>
                <span>Status: {(strategy.activeFlag || (strategy.active ? "Y" : "N")) === "Y" ? "Active" : "Inactive"}</span>
              </div>
            </div>

            {strategy.description && <p className="strategy-description">{strategy.description}</p>}

            <div className="table-wrapper">
              <table className="attempt-log-table confluence-rule-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>TF</th>
                    <th>Condition</th>
                    <th>Signal Key</th>
                    <th>Expected Direction</th>
                    <th>Latch</th>
                    <th>Max Age</th>
                    <th>Required</th>
                    <th>Reset On Final Trigger</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {ensureArray(strategy.rules).map((rule) => (
                    <tr key={rule.id || `${strategy.strategyCode}-${rule.ruleOrder}-${rule.signalKey}`}>
                      <td>{display(rule.stageNumber || rule.ruleOrder || rule.displayOrder)}</td>
                      <td>{display(rule.timeFrame)}</td>
                      <td>{display(rule.conditionType)}</td>
                      <td>{display(rule.signalKey)}</td>
                      <td>{display(rule.expectedDirection)}</td>
                      <td>{rule.conditionType === "LATCHED_EVENT" ? "Yes" : "No"}</td>
                      <td>{display(rule.maxAgeCandles)}</td>
                      <td>{rule.required === false ? "No" : "Yes"}</td>
                      <td>{rule.resetOnFinalTrigger ? "Yes" : "No"}</td>
                      <td className="rule-description-cell">{display(rule.ruleDescription)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default TradeEntryConfluenceStrategyTable;
